import { useState, useEffect } from 'react';
import { API, formatWeight, sortBoxesNatural, naturalBoxSort, displayDate } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { CheckCircle, XCircle, ArrowLeft, Sun, Moon, AlertTriangle, ClipboardCheck, Clock, Trash2, Pencil, Save, RefreshCw, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

const TOLERANCE_G = 0.150;

function diffColor(diff_g) {
  if (diff_g > TOLERANCE_G) return 'text-blue-400';       // increasing beyond tolerance
  if (diff_g > 0) return 'text-emerald-400';              // in range: 0 to +0.150g (dark green)
  if (diff_g >= -TOLERANCE_G) return 'text-emerald-300';  // in range: -0.150g to 0 (light green)
  return 'text-rose-400';                                 // decreasing beyond tolerance
}

function isDiffTolerable(diff_g) {
  return Math.abs(diff_g) <= TOLERANCE_G;
}

function round3(v) {
  return Math.round(v * 1000) / 1000;
}

export default function StockTakingResultsPage() {
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst';
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingBox, setEditingBox] = useState(null);
  const [editWeight, setEditWeight] = useState('');
  const [recalculating, setRecalculating] = useState(false);

  const fetchSessions = () => {
    API.get('/stock-taking/sessions').then(r => setSessions(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(); }, []);

  const loadDetail = async (session) => {
    setSelectedSession(session);
    setDetailLoading(true);
    try {
      const r = await API.get('/stock-taking/session-detail', { params: { date: session.date, stock_time: session.stock_time } });
      setDetail(r.data);
    } catch (e) { toast.error('Failed to load session detail'); }
    finally { setDetailLoading(false); }
  };

  const goBack = () => { setSelectedSession(null); setDetail(null); setEditingBox(null); };

  const handleDeleteSession = async () => {
    if (!selectedSession) return;
    try {
      await API.delete('/stock-taking/session', { params: { date: selectedSession.date, stock_time: selectedSession.stock_time } });
      toast.success('Session deleted');
      goBack();
      fetchSessions();
    } catch (e) { toast.error('Failed to delete session'); }
  };

  const startEdit = (boxId, currentWeight) => {
    setEditingBox(boxId);
    setEditWeight(String(currentWeight));
  };

  const handleSaveWeight = async (boxId) => {
    if (!selectedSession) return;
    try {
      await API.put('/stock-taking/update-weight', {
        box_id: boxId, date: selectedSession.date, stock_time: selectedSession.stock_time,
        gross_weight_g: parseFloat(editWeight),
      });
      toast.success(`Weight updated for ${boxId}`);
      setEditingBox(null);
      loadDetail(selectedSession);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update weight'); }
  };

  const handleRecalculate = async () => {
    if (!selectedSession) return;
    setRecalculating(true);
    try {
      const r = await API.post('/stock-taking/recalculate-book-values', {
        date: selectedSession.date, stock_time: selectedSession.stock_time,
      });
      toast.success(`Recalculated book values for ${r.data.updated} boxes`);
      loadDetail(selectedSession);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to recalculate'); }
    finally { setRecalculating(false); }
  };

  const handleExportSession = async () => {
    if (!selectedSession) return;
    try {
      const res = await API.get('/stock-taking/export', {
        params: { date: selectedSession.date, stock_time: selectedSession.stock_time },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock_taking_${selectedSession.date}_${selectedSession.stock_time}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="text-muted-foreground">Loading...</div></div>;

  // Detail View
  if (selectedSession && detail) {
    const comparisons = (detail.comparisons || []).sort((a, b) => naturalBoxSort(a.box_id, b.box_id));
    const checks = detail.four_box_checks || [];
    const discrepancies = comparisons.filter(c => c.difference_g !== 0 && !isDiffTolerable(c.difference_g));
    const tolerableDisc = comparisons.filter(c => c.difference_g !== 0 && isDiffTolerable(c.difference_g));
    const matched = comparisons.filter(c => c.difference_g === 0);

    // Group by purity
    const byPurity = {};
    comparisons.forEach(c => {
      const p = c.purity || 'Other';
      if (!byPurity[p]) byPurity[p] = [];
      byPurity[p].push(c);
    });

    return (
      <div data-testid="stock-taking-results-detail">
        <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <Button variant="ghost" size="sm" onClick={goBack} className="mb-1 -ml-2 gap-1" data-testid="results-back-btn">
              <ArrowLeft size={16} /> Back to Sessions
            </Button>
            <h1 className="flex items-center gap-2">
              Stock Taking: {displayDate(selectedSession.date)}
              <Badge variant="outline" className="text-xs">
                {selectedSession.stock_time === 'morning' ? <Sun size={12} className="mr-1" /> : <Moon size={12} className="mr-1" />}
                {selectedSession.stock_time}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              By {detail.session?.submitted_by || 'Unknown'} ({detail.session?.submitted_by_role}) - {detail.total_recorded}/{detail.total_active_boxes} boxes recorded
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExportSession} data-testid="export-session-btn">
              <Download size={14} /> Export Excel
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleRecalculate} disabled={recalculating} data-testid="recalculate-book-btn">
              <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} /> {recalculating ? 'Recalculating...' : 'Recalculate Book Values'}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-1.5" data-testid="delete-session-btn">
                  <Trash2 size={14} /> Delete Session
                </Button>
              </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Stock Taking Session?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all {detail.total_recorded} snapshots and 4-box checks for {displayDate(selectedSession.date)} ({selectedSession.stock_time}). Staff will need to redo stock taking.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteSession} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="pt-4 px-3">
              <p className="text-xs text-muted-foreground">Boxes Recorded</p>
              <p className="text-2xl font-bold font-mono" data-testid="results-boxes-recorded">{detail.total_recorded}<span className="text-sm text-muted-foreground font-normal">/{detail.total_active_boxes}</span></p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-3">
              <p className="text-xs text-muted-foreground">Weight Matches</p>
              <p className="text-2xl font-bold font-mono text-emerald-500" data-testid="results-weight-matches">{matched.length + tolerableDisc.length}</p>
              {tolerableDisc.length > 0 && <p className="text-[10px] text-muted-foreground">{tolerableDisc.length} within tolerance</p>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-3">
              <p className="text-xs text-muted-foreground">Discrepancies</p>
              <p className={`text-2xl font-bold font-mono ${discrepancies.length > 0 ? 'text-rose-500' : 'text-emerald-500'}`} data-testid="results-discrepancies">{discrepancies.length}</p>
              <p className="text-[10px] text-muted-foreground">beyond {TOLERANCE_G}g tolerance</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-3">
              <p className="text-xs text-muted-foreground">4-Box Checks</p>
              <p className="text-2xl font-bold font-mono" data-testid="results-4box-checks">
                <span className="text-emerald-500">{checks.filter(c => c.matches).length}</span>
                <span className="text-muted-foreground text-sm">/</span>
                <span className={checks.filter(c => !c.matches).length > 0 ? 'text-rose-500' : ''}>{checks.length}</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Purity-wise Summary */}
        {!isAnalyst && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Purity-wise Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="table-scroll-wrapper">
              <Table className="data-table" data-testid="results-purity-summary">
                <TableHeader>
                  <TableRow>
                    <TableHead>Purity</TableHead>
                    <TableHead>Boxes</TableHead>
                    <TableHead>Total Recorded</TableHead>
                    <TableHead>Total System (Book)</TableHead>
                    <TableHead>Total Difference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['76', '84', '92', 'Other'].map(purity => {
                    const items = byPurity[purity];
                    if (!items || items.length === 0) return null;
                    const totalRec = items.reduce((s, c) => s + c.recorded_gross_g, 0);
                    const totalSys = items.reduce((s, c) => s + c.system_gross_g, 0);
                    const totalDiff = round3(totalRec - totalSys);
                    return (
                      <TableRow key={purity}>
                        <TableCell><span className={`status-badge purity-${purity}`}>{purity}</span></TableCell>
                        <TableCell className="font-mono">{items.length}</TableCell>
                        <TableCell className="font-mono">{formatWeight(round3(totalRec))}</TableCell>
                        <TableCell className="font-mono">{formatWeight(round3(totalSys))}</TableCell>
                        <TableCell className={`font-mono font-semibold ${totalDiff === 0 ? 'text-emerald-500' : diffColor(totalDiff)}`}>
                          {totalDiff === 0 ? '-' : `${totalDiff > 0 ? '+' : ''}${formatWeight(totalDiff)}`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Grand Total */}
                  {(() => {
                    const grandRec = comparisons.reduce((s, c) => s + c.recorded_gross_g, 0);
                    const grandSys = comparisons.reduce((s, c) => s + c.system_gross_g, 0);
                    const grandDiff = round3(grandRec - grandSys);
                    return (
                      <TableRow className="border-t-2 border-border font-semibold">
                        <TableCell>Grand Total</TableCell>
                        <TableCell className="font-mono">{comparisons.length}</TableCell>
                        <TableCell className="font-mono">{formatWeight(round3(grandRec))}</TableCell>
                        <TableCell className="font-mono">{formatWeight(round3(grandSys))}</TableCell>
                        <TableCell className={`font-mono font-bold ${grandDiff === 0 ? 'text-emerald-500' : diffColor(grandDiff)}`}>
                          {grandDiff === 0 ? '-' : `${grandDiff > 0 ? '+' : ''}${formatWeight(grandDiff)}`}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Discrepancies Section (only non-tolerable differences) */}
        {discrepancies.length > 0 && (
          <Card className="mb-4 border-rose-800/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-rose-400">
                <AlertTriangle size={16} /> Weight Discrepancies Beyond Tolerance ({discrepancies.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="table-scroll-wrapper">
                <Table className="data-table" data-testid="results-discrepancies-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Box ID</TableHead>
                      <TableHead>Purity</TableHead>
                      <TableHead>Recorded Gross</TableHead>
                      <TableHead>System Gross</TableHead>
                      <TableHead>Difference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discrepancies.map(c => (
                      <TableRow key={c.box_id} className={c.difference_g > 0 ? 'bg-blue-950/10' : 'bg-rose-950/10'}>
                        <TableCell className="font-mono font-semibold">{c.box_id}</TableCell>
                        <TableCell><span className={`status-badge purity-${c.purity}`}>{c.purity}</span></TableCell>
                        <TableCell className="font-mono">{formatWeight(c.recorded_gross_g)}</TableCell>
                        <TableCell className="font-mono">{formatWeight(c.system_gross_g)}</TableCell>
                        <TableCell className={`font-mono font-semibold ${diffColor(c.difference_g)}`}>
                          {c.difference_g > 0 ? '+' : ''}{formatWeight(c.difference_g)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4-Box Check Results */}
        {checks.length > 0 && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">4-Box Group Check Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4" data-testid="results-4box-list">
                {checks.map((c, i) => {
                  const groupVsIndiv = c.matches;
                  const groupVsBook = c.group_vs_book_matches;
                  const indivVsBook = c.individual_total_g === c.book_total_g;
                  const allMatch = groupVsIndiv && groupVsBook;
                  const stockError = !groupVsIndiv && !indivVsBook;
                  return (
                    <div key={c.id || i} className={`p-4 rounded-lg border-2 ${allMatch ? 'border-emerald-800/40 bg-emerald-950/10' : 'border-rose-800/40 bg-rose-950/10'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {allMatch ? <CheckCircle className="text-emerald-500" size={18} /> : <AlertTriangle className="text-rose-500" size={18} />}
                          <span className="font-semibold text-sm">{allMatch ? 'ALL MATCH' : 'DISCREPANCY FOUND'}</span>
                          {stockError && <Badge variant="outline" className="text-xs text-rose-400 border-rose-600">Possible stock-taking error</Badge>}
                        </div>
                        <span className="font-mono text-xs text-muted-foreground">{c.box_ids?.join(' + ')}</span>
                      </div>
                      {/* Three-way comparison table */}
                      <div className="table-scroll-wrapper">
                        <Table className="data-table text-xs">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Comparison</TableHead>
                              <TableHead className="text-xs">Weight</TableHead>
                              <TableHead className="text-xs">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="text-muted-foreground">Group weight (physical)</TableCell>
                              <TableCell className="font-mono font-semibold">{formatWeight(c.group_weight_g)}</TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="text-muted-foreground">Sum of individual recorded</TableCell>
                              <TableCell className="font-mono font-semibold">{formatWeight(c.individual_total_g)}</TableCell>
                              <TableCell>
                                {groupVsIndiv
                                  ? <span className="flex items-center gap-1 text-emerald-500"><CheckCircle size={12} /> Matches group</span>
                                  : <span className="flex items-center gap-1 text-rose-400"><XCircle size={12} /> Diff: {formatWeight(c.difference_g)}</span>
                                }
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="text-muted-foreground">Sum of book (system) weights</TableCell>
                              <TableCell className="font-mono font-semibold">{formatWeight(c.book_total_g)}</TableCell>
                              <TableCell>
                                {groupVsBook
                                  ? <span className="flex items-center gap-1 text-emerald-500"><CheckCircle size={12} /> Matches group</span>
                                  : <span className="flex items-center gap-1 text-rose-400"><XCircle size={12} /> Diff: {c.group_vs_book_diff_g > 0 ? '+' : ''}{formatWeight(c.group_vs_book_diff_g)}</span>
                                }
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                      {/* Per-box breakdown */}
                      {c.box_details && c.box_details.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border/40">
                          <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">Per-box breakdown</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {c.box_details.map(bd => {
                              const bdDiff = round3(bd.recorded_g - bd.book_g);
                              return (
                                <div key={bd.box_id} className="p-2 rounded bg-muted/30 text-xs">
                                  <span className="font-mono font-semibold">{bd.box_id}</span>
                                  <div className="flex justify-between mt-1">
                                    <span className="text-muted-foreground">Rec:</span>
                                    <span className="font-mono">{formatWeight(bd.recorded_g)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Book:</span>
                                    <span className="font-mono">{formatWeight(bd.book_g)}</span>
                                  </div>
                                  {bdDiff !== 0 && (
                                    <div className={`flex justify-between font-semibold ${diffColor(bdDiff)}`}>
                                      <span>Diff:</span>
                                      <span className="font-mono">{bdDiff > 0 ? '+' : ''}{formatWeight(bdDiff)}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full Box-wise Comparison by Purity */}
        {['76', '84', '92', 'Other'].map(purity => {
          const items = byPurity[purity];
          if (!items || items.length === 0) return null;
          return (
            <Card key={purity} className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className={`status-badge purity-${purity}`}>Purity {purity}</span>
                  <span className="text-xs text-muted-foreground font-normal">{items.length} boxes</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="table-scroll-wrapper">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Box ID</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Recorded</TableHead>
                        <TableHead>System</TableHead>
                        <TableHead>Diff</TableHead>
                        <TableHead>Verified</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((c, idx) => (
                        <TableRow key={c.box_id} className={c.difference_g !== 0 && !isDiffTolerable(c.difference_g) ? (c.difference_g > 0 ? 'bg-blue-950/10' : 'bg-rose-950/5') : ''}>
                          <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                          <TableCell className="font-mono font-semibold">{c.box_id}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{c.description || '-'}</TableCell>
                          <TableCell>
                            {editingBox === c.box_id ? (
                              <div className="flex items-center gap-1">
                                <Input type="text" inputMode="decimal" value={editWeight}
                                  onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setEditWeight(v); }}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveWeight(c.box_id); if (e.key === 'Escape') setEditingBox(null); }}
                                  className="h-7 w-28 font-mono text-xs" autoFocus />
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleSaveWeight(c.box_id)}><Save size={12} /></Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono">{formatWeight(c.recorded_gross_g)}</span>
                                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => startEdit(c.box_id, c.recorded_gross_g)} data-testid={`edit-weight-${c.box_id}`}>
                                  <Pencil size={12} />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono">{formatWeight(c.system_gross_g)}</TableCell>
                          <TableCell className={`font-mono font-semibold ${c.difference_g === 0 ? 'text-emerald-500' : diffColor(c.difference_g)}`}>
                            {c.difference_g === 0 ? '-' : `${c.difference_g > 0 ? '+' : ''}${formatWeight(c.difference_g)}`}
                          </TableCell>
                          <TableCell>
                            {c.is_verified ? <CheckCircle size={14} className="text-emerald-500" /> : <span className="text-muted-foreground text-xs">-</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Purity totals row */}
                      {!isAnalyst && (() => {
                        const tRec = items.reduce((s, c) => s + c.recorded_gross_g, 0);
                        const tSys = items.reduce((s, c) => s + c.system_gross_g, 0);
                        const tDiff = round3(tRec - tSys);
                        return (
                          <TableRow className="border-t-2 border-border bg-muted/30 font-semibold">
                            <TableCell></TableCell>
                            <TableCell className="font-semibold text-xs">TOTAL</TableCell>
                            <TableCell></TableCell>
                            <TableCell className="font-mono font-bold">{formatWeight(round3(tRec))}</TableCell>
                            <TableCell className="font-mono font-bold">{formatWeight(round3(tSys))}</TableCell>
                            <TableCell className={`font-mono font-bold ${tDiff === 0 ? 'text-emerald-500' : diffColor(tDiff)}`}>
                              {tDiff === 0 ? '-' : `${tDiff > 0 ? '+' : ''}${formatWeight(tDiff)}`}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Sessions List View
  return (
    <div data-testid="stock-taking-results-page">
      <div className="page-header">
        <h1>Stock Taking Results</h1>
        <p className="hidden sm:block text-sm text-muted-foreground">Review finalized stock-taking sessions, discrepancies, and 4-box check results</p>
      </div>

      {sessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No finalized stock-taking sessions yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Sessions appear here after staff completes and finalizes stock taking.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => {
            const hasIssues = s.four_box_mismatches > 0 || s.discrepancy_count > 0;
            return (
              <Card
                key={`${s.date}-${s.stock_time}`}
                className={`cursor-pointer transition-colors hover:border-primary/40 ${hasIssues ? 'border-rose-800/30' : ''} ${!s.finalized ? 'border-amber-800/30' : ''}`}
                onClick={() => loadDetail(s)}
                data-testid={`session-${s.date}-${s.stock_time}`}
              >
                <CardContent className="py-4 px-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        {s.stock_time === 'morning' ? <Sun size={16} className="text-amber-400" /> : <Moon size={16} className="text-indigo-400" />}
                        <span className="font-semibold">{displayDate(s.date)}</span>
                        <Badge variant="outline" className="text-xs capitalize">{s.stock_time}</Badge>
                        {!s.finalized && <Badge variant="outline" className="text-xs text-amber-400 border-amber-600"><Clock size={10} className="mr-1" />Pending</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground">by {s.submitted_by}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-mono">{s.box_count}/{s.total_active_boxes} boxes</span>
                      <span className="flex items-center gap-1">
                        <CheckCircle size={12} className="text-emerald-500" />
                        {s.four_box_matches} matched
                      </span>
                      {s.four_box_mismatches > 0 && (
                        <span className="flex items-center gap-1 text-rose-400">
                          <XCircle size={12} />
                          {s.four_box_mismatches} mismatched
                        </span>
                      )}
                      {s.discrepancy_count != null && s.discrepancy_count > 0 && (
                        <span className="flex items-center gap-1 text-amber-400">
                          <AlertTriangle size={12} />
                          {s.discrepancy_count} discrepancies
                        </span>
                      )}
                      {s.discrepancy_count == null && (
                        <span className="text-xs text-muted-foreground italic">not reviewed</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
